import React, { useState, useMemo } from 'react';
import { Search, X, Filter, SlidersHorizontal, Calendar, User, Stethoscope, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export interface SimpleFilterState {
  searchQuery: string;
  dateFilter: 'all' | 'today' | 'week' | 'month';
  dateRange: { from: Date | null; to: Date | null };
  selectedMedicines: string[];
  selectedComplaints: string[];
  selectedPatients: string[];
}

interface SimpleFiltersProps {
  prescriptions: any[];
  patients: any[];
  onFiltersChange: (filters: SimpleFilterState) => void;
  onExport: (format: 'csv' | 'pdf' | 'excel') => void;
  totalResults: number;
  itemsPerPage: number;
  onItemsPerPageChange: (value: number) => void;
  className?: string;
}

export default function SimpleFilters({
  prescriptions,
  patients,
  onFiltersChange,
  onExport,
  totalResults,
  itemsPerPage,
  onItemsPerPageChange,
  className
}: SimpleFiltersProps) {
  const [filters, setFilters] = useState<SimpleFilterState>({
    searchQuery: '',
    dateFilter: 'all',
    dateRange: { from: null, to: null },
    selectedMedicines: [],
    selectedComplaints: [],
    selectedPatients: []
  });

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Generate filter options from prescription data
  const filterOptions = useMemo(() => {
    const medicines = new Set<string>();
    const complaints = new Set<string>();
    const patientNames = new Set<string>();

    prescriptions.forEach(prescription => {
      // Parse medicines
      if (prescription.medicines) {
        const medicineList = typeof prescription.medicines === 'string' 
          ? JSON.parse(prescription.medicines || '[]')
          : prescription.medicines;
        if (Array.isArray(medicineList)) {
          medicineList.forEach(med => {
            const medicineName = med?.product || med?.name || med?.medicine || '';
            if (medicineName) medicines.add(medicineName);
          });
        }
      }

      // Parse complaints
      if (prescription.complaints) {
        const complaintList = typeof prescription.complaints === 'string'
          ? JSON.parse(prescription.complaints || '[]')
          : prescription.complaints;
        if (Array.isArray(complaintList)) {
          complaintList.forEach(complaint => {
            const complaintText = typeof complaint === 'string' ? complaint : complaint.complaint;
            if (complaintText) complaints.add(complaintText);
          });
        }
      }
    });

    // Get patient names
    patients.forEach(patient => {
      if (patient.name) patientNames.add(patient.name);
    });

    return {
      medicines: Array.from(medicines).sort(),
      complaints: Array.from(complaints).sort(),
      patients: Array.from(patientNames).sort()
    };
  }, [prescriptions, patients]);

  // Search suggestions
  const searchSuggestions = useMemo(() => {
    if (!filters.searchQuery || filters.searchQuery.length < 2) return [];
    
    const query = filters.searchQuery.toLowerCase();
    const suggestions = new Set<string>();
    
    // Patient names
    patients.forEach(patient => {
      if (patient.name?.toLowerCase().includes(query)) {
        suggestions.add(patient.name);
      }
      if (patient.phone?.includes(query)) {
        suggestions.add(patient.phone);
      }
    });

    // Medicine names
    filterOptions.medicines.forEach(medicine => {
      if (medicine.toLowerCase().includes(query)) {
        suggestions.add(medicine);
      }
    });

    // Complaint names
    filterOptions.complaints.forEach(complaint => {
      if (complaint.toLowerCase().includes(query)) {
        suggestions.add(complaint);
      }
    });

    return Array.from(suggestions).slice(0, 5);
  }, [filters.searchQuery, patients, filterOptions]);

  const updateFilters = (newFilters: Partial<SimpleFilterState>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    onFiltersChange(updatedFilters);
  };

  const handleSearchChange = (value: string) => {
    updateFilters({ searchQuery: value });
    setShowSuggestions(value.length >= 2 && searchSuggestions.length > 0);
  };

  const handleClearAllFilters = () => {
    const resetFilters: SimpleFilterState = {
      searchQuery: '',
      dateFilter: 'all',
      dateRange: { from: null, to: null },
      selectedMedicines: [],
      selectedComplaints: [],
      selectedPatients: []
    };
    setFilters(resetFilters);
    onFiltersChange(resetFilters);
    setShowAdvancedFilters(false);
    setShowSuggestions(false);
  };

  // Check if any filters are active
  const hasActiveFilters = filters.searchQuery || 
    filters.dateFilter !== 'all' || 
    filters.dateRange.from || 
    filters.dateRange.to ||
    filters.selectedMedicines.length > 0 ||
    filters.selectedComplaints.length > 0 ||
    filters.selectedPatients.length > 0;

  return (
    <Card className={cn("medical-card", className)}>
      <CardContent className="p-3 sm:p-4 space-y-3">
        {/* Top Row: Search + Quick Filters */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          {/* Enhanced Search Input */}
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by patient name, phone, medicines, complaints..."
              value={filters.searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              onFocus={() => {
                if (filters.searchQuery && searchSuggestions.length > 0) {
                  setShowSuggestions(true);
                }
              }}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              className="pl-10 pr-10 h-9 text-sm font-medium transition-colors shadow-sm bg-background border-2 border-border focus:border-primary"
            />
            
            {/* Clear Search */}
            {filters.searchQuery && (
              <button
                onClick={() => {
                  updateFilters({ searchQuery: '' });
                  setShowSuggestions(false);
                }}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}

            {/* Search Suggestions Dropdown */}
            {showSuggestions && searchSuggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-background border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                {searchSuggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      updateFilters({ searchQuery: suggestion });
                      setShowSuggestions(false);
                    }}
                    className="w-full text-left px-3 py-2 hover:bg-accent hover:text-accent-foreground transition-colors text-sm border-b border-border/50 last:border-b-0"
                  >
                    <div className="flex items-center gap-2">
                      <Search className="w-3 h-3 text-muted-foreground" />
                      <span className="truncate">{suggestion}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Quick Date Filter Buttons + Advanced Toggle */}
          <div className="flex items-center gap-1.5 sm:gap-2 justify-stretch sm:justify-start">
            <Button
              variant={filters.dateFilter === 'today' ? 'default' : 'outline'}
              size="sm"
              onClick={() => updateFilters({ 
                dateFilter: filters.dateFilter === 'today' ? 'all' : 'today',
                dateRange: { from: null, to: null }
              })}
              className="h-9 px-3 sm:px-4 font-medium flex-1 sm:flex-none min-w-16 shadow-sm border-2"
            >
              Today
            </Button>
            <Button
              variant={filters.dateFilter === 'week' ? 'default' : 'outline'}
              size="sm"
              onClick={() => updateFilters({ 
                dateFilter: filters.dateFilter === 'week' ? 'all' : 'week',
                dateRange: { from: null, to: null }
              })}
              className="h-9 px-3 sm:px-4 font-medium flex-1 sm:flex-none min-w-16 shadow-sm border-2"
            >
              Week
            </Button>
            <Button
              variant={filters.dateFilter === 'month' ? 'default' : 'outline'}
              size="sm"
              onClick={() => updateFilters({ 
                dateFilter: filters.dateFilter === 'month' ? 'all' : 'month',
                dateRange: { from: null, to: null }
              })}
              className="h-9 px-3 sm:px-4 font-medium flex-1 sm:flex-none min-w-16 shadow-sm border-2"
            >
              Month
            </Button>
            
            {/* Advanced Filters Toggle */}
            <Button
              variant={showAdvancedFilters ? "default" : "outline"}
              size="sm"
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="h-9 px-3 border-2"
            >
              <SlidersHorizontal className="w-4 h-4 mr-1" />
              Filters
            </Button>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div className="border border-border rounded-lg p-4 bg-muted/20 space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Date Range Filters */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Custom Date Range
                  </label>
                  {(filters.dateRange.from || filters.dateRange.to) && (
                    <button
                      onClick={() => updateFilters({ 
                        dateRange: { from: null, to: null },
                        dateFilter: 'all'
                      })}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      Clear Date
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">From Date</label>
                    <Input
                      type="date"
                      value={filters.dateRange.from ? filters.dateRange.from.toISOString().split('T')[0] : ''}
                      onChange={(e) => {
                        updateFilters({
                          dateRange: {
                            from: e.target.value ? new Date(e.target.value) : null,
                            to: filters.dateRange.to
                          },
                          dateFilter: 'all'
                        });
                      }}
                      className="h-9 text-sm"
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">To Date</label>
                    <Input
                      type="date"
                      value={filters.dateRange.to ? filters.dateRange.to.toISOString().split('T')[0] : ''}
                      onChange={(e) => {
                        updateFilters({
                          dateRange: {
                            from: filters.dateRange.from,
                            to: e.target.value ? new Date(e.target.value) : null
                          },
                          dateFilter: 'all'
                        });
                      }}
                      className="h-9 text-sm"
                      min={filters.dateRange.from ? filters.dateRange.from.toISOString().split('T')[0] : undefined}
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>
              </div>

              {/* Medicine & Complaint Filters */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="text-sm font-medium text-foreground flex items-center gap-2">
                    <Stethoscope className="w-4 h-4" />
                    Medical Filters
                  </label>
                  {(filters.selectedMedicines.length > 0 || filters.selectedComplaints.length > 0) && (
                    <button
                      onClick={() => updateFilters({ 
                        selectedMedicines: [],
                        selectedComplaints: []
                      })}
                      className="text-xs text-muted-foreground hover:text-foreground"
                    >
                      Clear Medical
                    </button>
                  )}
                </div>
                
                {/* Medicine Filter - Show top 5 medicines as buttons */}
                {filterOptions.medicines.length > 0 && (
                  <div className="space-y-2 mb-3">
                    <div className="text-xs text-muted-foreground">Common Medicines</div>
                    <div className="flex flex-wrap gap-1">
                      {filterOptions.medicines.slice(0, 5).map((medicine) => {
                        const isActive = filters.selectedMedicines.includes(medicine);
                        return (
                          <button
                            key={medicine}
                            onClick={() => {
                              const newMedicines = isActive
                                ? filters.selectedMedicines.filter(m => m !== medicine)
                                : [...filters.selectedMedicines, medicine];
                              updateFilters({ selectedMedicines: newMedicines });
                            }}
                            className={cn(
                              "px-2 py-1 text-xs rounded-md border transition-colors",
                              isActive 
                                ? "bg-medical-medicines text-white border-medical-medicines shadow-sm" 
                                : "border-border hover:bg-accent hover:border-medical-medicines/50 hover:text-medical-medicines"
                            )}
                          >
                            {medicine}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Complaint Filter - Show top 5 complaints as buttons */}
                {filterOptions.complaints.length > 0 && (
                  <div className="space-y-2">
                    <div className="text-xs text-muted-foreground">Common Complaints</div>
                    <div className="flex flex-wrap gap-1">
                      {filterOptions.complaints.slice(0, 5).map((complaint) => {
                        const isActive = filters.selectedComplaints.includes(complaint);
                        return (
                          <button
                            key={complaint}
                            onClick={() => {
                              const newComplaints = isActive
                                ? filters.selectedComplaints.filter(c => c !== complaint)
                                : [...filters.selectedComplaints, complaint];
                              updateFilters({ selectedComplaints: newComplaints });
                            }}
                            className={cn(
                              "px-2 py-1 text-xs rounded-md border transition-colors",
                              isActive 
                                ? "bg-medical-complaints text-white border-medical-complaints shadow-sm" 
                                : "border-border hover:bg-accent hover:border-medical-complaints/50 hover:text-medical-complaints"
                            )}
                          >
                            {complaint}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Filter Results Summary */}
        {hasActiveFilters && (
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-3 bg-gradient-to-r from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/5 rounded-lg border border-primary/20 dark:border-primary/30">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <div className="flex items-center gap-2 text-primary font-medium">
                <Filter className="w-3 h-3" />
                <span>
                  Showing <span className="font-bold">{totalResults}</span> prescriptions with active filters
                </span>
              </div>
              
              {/* Active Filter Tags */}
              <div className="flex flex-wrap items-center gap-1 text-xs">
                {filters.searchQuery && (
                  <span className="bg-accent text-accent-foreground px-2 py-1 rounded-full border border-border">
                    Search: "{filters.searchQuery}"
                  </span>
                )}
                {filters.dateFilter !== 'all' && (
                  <span className="bg-accent text-accent-foreground px-2 py-1 rounded-full border border-border capitalize">
                    {filters.dateFilter === 'today' ? 'Today' : filters.dateFilter === 'week' ? 'This Week' : 'This Month'}
                  </span>
                )}
                {(filters.dateRange.from || filters.dateRange.to) && (
                  <span className="bg-accent text-accent-foreground px-2 py-1 rounded-full border border-border">
                    Custom Date Range
                  </span>
                )}
                {filters.selectedMedicines.length > 0 && (
                  <span className="bg-medical-medicines/10 text-medical-medicines px-2 py-1 rounded-full border border-medical-medicines/20">
                    {filters.selectedMedicines.length} Medicine{filters.selectedMedicines.length > 1 ? 's' : ''}
                  </span>
                )}
                {filters.selectedComplaints.length > 0 && (
                  <span className="bg-medical-complaints/10 text-medical-complaints px-2 py-1 rounded-full border border-medical-complaints/20">
                    {filters.selectedComplaints.length} Complaint{filters.selectedComplaints.length > 1 ? 's' : ''}
                  </span>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClearAllFilters}
              className="h-7 px-3 text-primary hover:text-primary-foreground hover:bg-primary border border-primary/30 shrink-0 transition-colors"
            >
              <X className="w-3 h-3 mr-1" />
              Clear All
            </Button>
          </div>
        )}

        {/* Results Count and Items Per Page */}
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4" />
            <span>
              {totalResults} prescription{totalResults !== 1 ? 's' : ''} found
            </span>
          </div>
          
          <div className="flex items-center gap-2">
            <span>Show:</span>
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              className="px-2 py-1 border border-border rounded text-sm bg-background"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span>per page</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
} 